import React, { useState, useEffect } from 'react';
import { useSession } from '../Utils/SessionContext';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
// import { getEvaluationStatusColorClass } from '../Utils/ColorUtils';
import { useLocation, useNavigate } from 'react-router-dom';
import CreateEvaluation from '../EvaluationComponents/CreateEvaluation';
import CreateEvaluationWizard from '../CreateEvaluationComponents/CreateEvaluationWizard';
import EvaluationStatus from '../EvaluationComponents/Utils/EvaluationStatus';

const JobEvaluations = ({ jobData }) => {
  // State for evaluations data
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for create evaluation popup
  const [showCreateEvalPopup, setShowCreateEvalPopup] = useState(false);
  
  // State for create evaluation wizard
  const [showCreateEvalWizard, setShowCreateEvalWizard] = useState(false);
  const [wizardEvaluationId, setWizardEvaluationId] = useState(null);
  
  // Hooks for session and navigation
  const { user, logout } = useSession();
  const location = useLocation();
  const navigate = useNavigate();

  // Extract job ID from URL query parameters
  const getJobId = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('jid');
  };

  // Function to fetch evaluations from the API
  const fetchEvaluations = async () => {
    const jobId = getJobId();
    
    if (!jobId) {
      setLoading(false);
      setError('Job ID is missing');
      return;
    }

    try {
      setLoading(true);
      
      // Check if user exists
      if (!user) {
        throw new Error('User session not available. Please log in again.');
      }
      
      // Make GET request to the evaluations endpoint with job ID as a parameter
      const url = new URL(API_ENDPOINTS.GET_JOB_EVALUATIONS);
      url.searchParams.append('jid', jobId);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.access_token}`
        }
      });
      
      // Check if the response is ok
      if (!response.ok) {
        const error = new Error(`Error: ${response.status} ${response.statusText}`);
        error.status = response.status;
        throw error;
      }
      
      // Parse the response data with error handling for empty responses
      let data = [];
      try {
        data = await response.json();
        console.log('Evaluations data:', data);
      } catch (parseError) {
        console.log('Empty or invalid response received for evaluations');
        data = [];
      }
      
      // Update evaluations state
      setEvaluations(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      console.error('Error fetching evaluations:', err);
      console.log('Error status code:', err.status);
      
      // If it's a 401 error, it's an expired session - log out and redirect to login
      if (err.status === 401) {
        console.log('Session expired or unauthorized. Redirecting to login page.');
        logout();
        navigate('/login');
        return;
      }
      
      setError(err.message || 'Failed to fetch evaluations');
    } finally {
      setLoading(false);
    }
  };

  // Function to handle opening the evaluation details page
  const handleOpenEvaluationDetails = (evaluationId) => {
    navigate(`/evaluation-details?eid=${evaluationId}`);
  };
  
  // Function to handle create evaluation submission
  const handleCreateEvaluation = async (data) => {
    console.log('Evaluation created:', data);
    
    // Refresh the evaluations list
    fetchEvaluations();
    
    // Show success message
    alert(`Evaluation "${data.evaluationName}" has been created successfully. It will be generated in approximately 10 minutes.`);
  };

  // Fetch evaluations when component mounts
  useEffect(() => {
    fetchEvaluations();
  }, [location]);

  return (
    <>
      <div className="space-y-4">
        {/* Header section - always visible */}
        <div className="bg-gray-100 shadow-sm rounded-lg p-6 mb-6 border border-gray-200">
          <div className="flex justify-between items-start">
            <div className="mr-8">
              <h2 className="text-2xl font-bold text-gray-900">Job Evaluation Templates</h2>
              <p className="text-gray-600 mt-2">
                This tab displays all evaluations available for this job position.
              </p>
            </div>
            <div className="flex items-center space-x-2">
              {/* Wizard Button */}
              <button 
                onClick={() => setShowCreateEvalWizard(true)}
                className="px-3 py-2 bg-blue-500 text-white font-semibold rounded hover:bg-blue-600 transition-colors flex items-center"
                title="Create Evaluation Wizard"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                Create New Evaluation
              </button>
              
              {/* Regular Create Evaluation Button */}
              {/* <button
                onClick={() => setShowCreateEvalPopup(true)}
                className="px-3 py-2 bg-blue-500 text-white font-semibold rounded hover:bg-blue-600 transition-colors flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                Create New Evaluation
              </button> */}
            </div>
          </div>
        </div>
      
        {/* Table - always visible */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-4 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                  NAME
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center">
                    <p className="text-gray-600">Loading evaluations...</p>
                  </td>
                </tr>
              )}
              
              {!loading && error && (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center">
                    <p className="text-red-500">{error}</p>
                    <button 
                      onClick={() => fetchEvaluations()}
                      className="mt-2 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Try Again
                    </button>
                  </td>
                </tr>
              )}
              
              {!loading && !error && evaluations.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center">
                    <p className="text-sm text-gray-500 mb-4">No evaluations found for this job position.</p>
                    <button
                      onClick={() => setShowCreateEvalWizard(true)}
                      className="px-4 py-2 bg-blue-500 text-white font-semibold rounded hover:bg-blue-600 transition-colors flex items-center mx-auto"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                      </svg>
                      Create New Evaluation
                    </button>
                  </td>
                </tr>
              )}
              
              {!loading && !error && evaluations.length > 0 && 
                evaluations.map((evaluation) => {
                  // Format the date
                  const createdDate = new Date(evaluation.created_at);
                  const formattedDate = createdDate.toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                  
                  return (
                    <tr 
                      key={evaluation.evaluation_id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleOpenEvaluationDetails(evaluation.evaluation_id)}
                    >
                      <td className="px-6 py-5 whitespace-nowrap text-base text-gray-900">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-bold">{evaluation.name}</p>
                            {evaluation.is_default && (
                              <svg 
                                className="w-5 h-5 text-green-500" 
                                fill="currentColor" 
                                viewBox="0 0 20 20" 
                                xmlns="http://www.w3.org/2000/svg"
                                title="Default Evaluation"
                              >
                                <path d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z"></path>
                              </svg>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">Evaluation ID: {evaluation.evaluation_id}</p>
                        </div>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap text-base text-gray-900">{evaluation.type}</td>
                      <td className="px-6 py-5 whitespace-nowrap text-base text-gray-900">{formattedDate}</td>
                      <td className="px-6 py-5 whitespace-nowrap text-base text-gray-900">
                        <EvaluationStatus status={evaluation.status} showDot={true} size="sm" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {/* Wizard Button - Only show if status is not evs_available */}
                          {evaluation.status !== 'evs_available' && (
                            <button 
                              className="p-2 bg-yellow-500 text-white rounded-full hover:bg-yellow-600 transition-colors"
                              title="Edit in Wizard"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent row click event
                                setWizardEvaluationId(evaluation.evaluation_id);
                                setShowCreateEvalWizard(true);
                              }}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                              </svg>
                            </button>
                          )}
                          
                          {/* Delete Button */}
                          <button 
                            className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                            title="Delete"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click event
                              
                              // Show confirmation dialog
                              if (window.confirm(`Are you sure you want to delete evaluation "${evaluation.name}"?`)) {
                                // User confirmed, send delete request
                                const deleteEvaluation = async () => {
                                  try {
                                    // Create URL with evaluation ID parameter
                                    const url = new URL(API_ENDPOINTS.DELETE_EVALUATION);
                                    url.searchParams.append('eid', evaluation.evaluation_id);
                                    
                                    const response = await fetch(url, {
                                      method: 'GET',
                                      headers: {
                                        'Authorization': `Bearer ${user.access_token}`
                                      }
                                    });
                                    
                                    if (!response.ok) {
                                      throw new Error(`Error: ${response.status} ${response.statusText}`);
                                    }
                                    
                                    // Remove the deleted evaluation from the list
                                    setEvaluations(prevEvaluations => 
                                      prevEvaluations.filter(item => item.evaluation_id !== evaluation.evaluation_id)
                                    );
                                  } catch (err) {
                                    console.error('Error deleting evaluation:', err);
                                    alert('Failed to delete evaluation. Please try again.');
                                  }
                                };
                                
                                deleteEvaluation();
                              }
                            }}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Create Evaluation Popup */}
      {showCreateEvalPopup && (
        <CreateEvaluation 
          isOpen={showCreateEvalPopup}
          onClose={() => setShowCreateEvalPopup(false)}
          onSubmit={handleCreateEvaluation}
          job={jobData ? 
            { job_id: jobData.id, 
              title: jobData.name, 
              company: jobData.companyName, 
              job_category_json: jobData.job_category_json, 
              status: jobData.status 
            } : null}
          talent={null}
          contextType="job"
        />
      )}

      {/* Create Evaluation Wizard Popup */}
      {showCreateEvalWizard && (
        <CreateEvaluationWizard 
          isOpen={showCreateEvalWizard}
          onClose={() => {
            setShowCreateEvalWizard(false);
            setWizardEvaluationId(null); // Reset the evaluation ID when closing
          }}
          evaluation_id={wizardEvaluationId}
          jobData={jobData}
        />
      )}
    </>
  );
};

export default JobEvaluations;
